import React, { useState, FormEvent } from "react";
import { Box, TextField, IconButton, CircularProgress } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { sendMessage } from "../../store/slices/messagingSlice";
import { RootState } from "../../store/store";

interface MessageInputProps {
    conversationId: string; // Expect the active conversation ID as a prop
}

const MessageInput: React.FC<MessageInputProps> = ({ conversationId }) => {
    const dispatch = useAppDispatch();
    const [messageContent, setMessageContent] = useState("");

    // Select status for sending messages to disable input/show loading
    const sendStatus = useAppSelector((state: RootState) => state.messaging.sendMessageStatus);
    const isLoading = sendStatus === "pending";

    // Handle message submission
    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent default form submission
        const trimmedContent = messageContent.trim();

        // Don't send empty messages or if already sending
        if (!trimmedContent || isLoading) {
            return;
        }

        console.log(`[MessageInput] Sending message to ${conversationId}: ${trimmedContent}`);
        try {
            // Dispatch the sendMessage thunk
            await dispatch(sendMessage({ conversationId, content: trimmedContent })).unwrap();
            // Clear the input field only on successful send
            setMessageContent("");
            console.log(`[MessageInput] Message sent successfully, input cleared.`);
        } catch (error) {
            // Error handling is mostly done in the slice/rejected reducer
            console.error("[MessageInput] Failed to send message:", error);
            // Optionally show a temporary error state to the user here
        }
    };

    return (
        <Box
            component="form" // Use form element for accessibility and enter key submission
            onSubmit={handleSubmit}
            sx={{
                display: "flex",
                alignItems: "center",
                padding: "8px 12px", // Add some padding
            }}
        >
            <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Type a message..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                disabled={isLoading} // Disable input while sending
                multiline // Allow multiple lines
                maxRows={4} // Limit the height increase
                sx={{ mr: 1 }} // Add margin to the right
                autoComplete="off"
            />
            <IconButton
                type="submit" // Trigger form submission
                color="primary"
                disabled={!messageContent.trim() || isLoading} // Disable if input is empty or loading
                aria-label="send message"
            >
                {/* Show loading spinner or send icon */}
                {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
        </Box>
    );
};

export default MessageInput;
